import {apiClient} from 'common/util/api-client';
import type {
  CohortSyncTime,
  CohortSyncTimeRequest,
  FeatureFlagApiPayload,
  FeatureFlagApiResponse,
  FeatureFlagHistoryData,
  FeatureFlagLimitsResults,
  FeatureFlagUsersRequest,
} from 'app_api/projects/feature_flags/types';

interface UseFeatureFlagsApiProps {
  projectId: string | number;
  workspaceId?: string | number;
}

type ListFeatureFlagsResponse = FeatureFlagApiResponse[];
type FeatureFlagHistoryResponse = FeatureFlagHistoryData;
type GetFeatureFlagRequest = {id: string};
type GetFeatureFlagHistoryRequest = {id: string; page?: string};
type CreateFeatureFlagRequest = FeatureFlagApiPayload;
type UpdateFeatureFlagRequest = FeatureFlagApiPayload & {id: string};
type DeleteFeatureFlagRequest = {id: string};
type DuplicateFeatureFlagRequest = {id: string};
type ArchiveFeatureFlagRequest = {id: string};
type RestoreFeatureFlagRequest = {id: string};
type GetFeatureFlagUsersRequest = FeatureFlagUsersRequest;
type GetCohortSyncTimeRequest = CohortSyncTimeRequest;
type CopyFeatureFlagToProjectRequest = {id: string; dest_project: string; dest_workspace?: string};
type PushFeatureFlagUpdatesToProjectRequest = {id: string; dest_project: string; dest_workspace?: string};
type ListFeatureFlagEnvironmentsRequest = {id: string};
type UnlinkFeatureFlagEnvironmentsRequest = {id: string; target_project: string; target_workspace?: string};

type FeatureFlagUsersResults = {
  [k: string]: Array<{
    [k: string]: any;
  }>;
};

export function featureFlagsApi(props: UseFeatureFlagsApiProps) {
  const basePath = props.workspaceId
    ? `/api/app/projects/${props.projectId}/workspaces/${props.workspaceId}/feature-flags`
    : `/api/app/projects/${props.projectId}/feature-flags`;
  return {
    listFeatureFlags: () => {
      // Always fetch all flags including archived - filtering is done client-side
      return apiClient.get<ListFeatureFlagsResponse>(`${basePath}?include_archived=true`);
    },

    getFeatureFlag: ({id}: GetFeatureFlagRequest) => {
      return apiClient.get<FeatureFlagApiResponse>(`${basePath}/${id}`);
    },

    getFeatureFlagHistory: ({id, page}: GetFeatureFlagHistoryRequest) => {
      const query = page ? `?page=` + page : ``;
      return apiClient.get<FeatureFlagHistoryResponse>(`${basePath}/${id}/history` + query);
    },

    createFeatureFlag: (flag: CreateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(basePath, flag);
    },

    updateFeatureFlag: ({id, ...flag}: UpdateFeatureFlagRequest) => {
      return apiClient.put<FeatureFlagApiResponse>(`${basePath}/${id}`, flag);
    },

    deleteFeatureFlag: ({id}: DeleteFeatureFlagRequest) => {
      return apiClient.delete(`${basePath}/${id}`);
    },

    duplicateFeatureFlag: ({id}: DuplicateFeatureFlagRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/duplicate`, {});
    },

    archiveFeatureFlag: ({id}: ArchiveFeatureFlagRequest) => {
      return apiClient.post(`${basePath}/${id}/archive`, {});
    },

    restoreFeatureFlag: ({id}: RestoreFeatureFlagRequest) => {
      return apiClient.delete<FeatureFlagApiResponse>(`${basePath}/${id}/archive`);
    },

    getFeatureFlagLimits: () => {
      return apiClient.get<FeatureFlagLimitsResults>(`${basePath}/limits`);
    },

    getFeatureFlagUsers: (request: GetFeatureFlagUsersRequest, signal?: AbortSignal) => {
      return apiClient.post<FeatureFlagUsersResults>(`${basePath}/users`, request, {abortSignal: signal});
    },

    getCohortSyncTime: (request: GetCohortSyncTimeRequest) => {
      return apiClient.post<CohortSyncTime>(`${basePath}/last-cohort-sync-time`, request);
    },

    copyFeatureFlagToProject: ({id, ...params}: CopyFeatureFlagToProjectRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/copy_to_project`, params);
    },

    pushFeatureFlagUpdatesToProject: ({id, ...params}: PushFeatureFlagUpdatesToProjectRequest) => {
      return apiClient.post<FeatureFlagApiResponse>(`${basePath}/${id}/push_updates_to_project`, params);
    },

    unlinkFeatureFlagEnvironments: ({id, ...params}: UnlinkFeatureFlagEnvironmentsRequest) => {
      const parentEntityPath = params.target_workspace
        ? `workspaces/${params.target_workspace}`
        : `projects/${params.target_project}`;
      const path = `/api/app/${parentEntityPath}/feature-flags`;
      return apiClient.post<FeatureFlagApiResponse>(`${path}/${id}/unlink_environments`, params);
    },

    listFeatureFlagEnvironments: ({id, ...params}: ListFeatureFlagEnvironmentsRequest) => {
      return apiClient.get<ListFeatureFlagsResponse>(`${basePath}/${id}/list_flag_environments`, params);
    },
  } as const;
}
